import { Box, Typography } from "@mui/material";
import startCase from "lodash/startCase";
import { chain } from "mathjs";
import moment from "moment";
import React, { JSX } from "react";
import { useSelector } from "react-redux";
import { scaleFont } from "../../../components/Print/Print";
import { tl } from "../../../components/translate";
import classNames from "../../../helpers/classNames";
import {
  BillItemType,
  BillType,
  DiscountBasedOn,
  DocumentTypes
} from "../../../interfaces/BillInterfaces";
import { spFromIdSelector, spFullNameSelectorWithNmc } from "../../../reducers/serviceProvider";
import { getSectionStyle } from "../../Lab/LabPrint/LabPrintFunctions";
import "./PrintBill.scss";
import {
  BillColumns,
  getBillColumns,
  getColumnTextStyles,
  hasItemWithHSCode,
  marginForNameColumn,
  showInfoColumn,
  showUnitAfterQuantity
} from "../../../actions/helpers/billHelper";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import BillPrintSummary from "../BillPrintSummary";
import capitalizedName from "../../../helpers/nameCapitalizer";
import { RootState } from "../../../store";
import { getPaymentMethodLabel } from "../../Reports/SalesReport";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { useShowStockBatchAndExpiryInBillPrint } from "../../../hooks/useShowStockBatchAndExpiryInBillPrint";

interface Props {
  billData: BillType;
  SPs: ServiceProvider[];
  relatedSettings: { [key: string]: boolean };
  clientBalance: number;
  hidePaymentMethodOnPrint?: boolean;
  isVatRegistered: boolean;
}

const DescribeBillV2 = ({
  billData,
  SPs,
  relatedSettings,
  isVatRegistered,
  clientBalance,
  hidePaymentMethodOnPrint = false
}: Props): JSX.Element | null => {
  // custom hooks
  const currentRc = useCurrentResourceCentre();
  const { showStockBatchAndExpiryInBillPrint } = useShowStockBatchAndExpiryInBillPrint();

  // selectors
  const user = useSelector((state: RootState) => state.userContext.user);

  // variables
  const showSubItemPriceOnPrint = currentRc?.settings?.billingSettings?.showSubItemPriceOnPrint;
  if (!billData || !billData.document) return null;

  // helper functions
  const showInfo = showInfoColumn(billData);
  const showHSCode = hasItemWithHSCode(billData);

  const billColumns = getBillColumns(showInfo).filter((header) => {
    if (header === BillColumns.hsCode) {
      return showHSCode;
    }

    if (header === BillColumns.DISCOUNT_AMT) {
      return !(
        [BillColumns.DISCOUNT_AMT].includes(header) &&
        billData.document?.settings.discountSettings.discountBasis === DiscountBasedOn.invoice
      );
    }
    if (header === BillColumns.VAT_PCT) {
      if (isVatRegistered) {
        return header;
      }
      return false;
    }
    return header;
  });

  let netTotal;
  if (billData.document.type === DocumentTypes.CREDITNOTE) {
    netTotal = billData.document.billItems.reduce(
      (sum, item) =>
        chain(sum)
          .add(
            chain(item.perUnit || 0)
              .multiply(item.quantity || 1)
              .add(item.discountAmt || 0)
              .done()
          )
          .done(),
      0
    );
  } else {
    netTotal = billData.document.billItems.reduce(
      (sum, item) =>
        chain(sum)
          .add(
            chain(item.perUnit || 0)
              .multiply(item.quantity || 1)
              .subtract(item.discountAmt || 0)
              .done()
          )
          .done(),
      0
    );
  }

  const spField = (item: BillItemType) =>
    Array.isArray(item?.serviceProviderIds) && item.serviceProviderIds.length > 0 ? (
      <Box component="span" sx={{ ml: "4px", fontSize: "10px" }}>
        (
        {item.serviceProviderIds
          .map((spId) => spFullNameSelectorWithNmc(spFromIdSelector(SPs, spId)))
          .join(", ")}
        )
      </Box>
    ) : (
      // for older bills with single sp id
      item?.serviceProviderId && (
        <Box component="span" sx={{ ml: "4px", fontSize: "10px" }}>
          ({spFullNameSelectorWithNmc(spFromIdSelector(SPs, item.serviceProviderId))})
        </Box>
      )
    );

  return (
    <Box>
      <Box>
        <Box
          display="flex"
          borderTop="1px solid #bbb"
          borderRight={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          borderBottom={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          borderLeft={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          py="0.3cm"
          className={!relatedSettings.isBordered ? "borderBotBlack1" : undefined}
          style={
            relatedSettings.isBordered
              ? getSectionStyle(relatedSettings.headerColor).rawHeaderStyle
              : {}
          }
        >
          {billColumns.map((column, i) => (
            <Box
              key={column}
              className={classNames(
                [BillColumns.SNO, BillColumns.QUANTITY, BillColumns.PER_UNIT].includes(column) &&
                  "flex1",
                [
                  BillColumns.hsCode,
                  BillColumns.DISCOUNT_AMT,
                  BillColumns.VAT_PCT,
                  BillColumns.GROSS_TOTAL
                ].includes(column) && "flex2",

                [BillColumns.DESCRIPTION, BillColumns.INFO].includes(column) && "flex3"
              )}
              sx={{
                mx: marginForNameColumn(column),
                px: i === 0 || i === billColumns.length - 1 ? "0.2cm" : ""
              }}
            >
              <Typography
                sx={{
                  fontSize: scaleFont("0.27cm"),
                  textTransform: "uppercase",
                  whiteSpace: "pre",
                  textAlign: getColumnTextStyles({ index: i, column }).textAlign,
                  fontWeight: 600
                }}
              >
                {tl(`billing.billItem.${column}`)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box paddingBottom="0.45cm">
          {[...billData.document.billItems]
            .sort(
              (a: Partial<BillItemType>, b: Partial<BillItemType>) => (a.sNo ?? 0) - (b.sNo ?? 0)
            )
            .map((item, index) => (
              <Box
                key={item.sNo}
                display="flex"
                style={
                  relatedSettings.isBordered
                    ? {
                        pageBreakInside: "avoid"
                      }
                    : {}
                }
                borderTop={
                  relatedSettings.isBordered && index !== 0 ? "0.5px solid lightgrey" : undefined
                }
                borderBottom={
                  index === billData.document.billItems.length - 1
                    ? relatedSettings.isBordered && "1px solid #bbb"
                    : relatedSettings.isBordered && "0.5px solid lightgrey"
                }
                className={
                  index === billData.document.billItems.length - 1 && !relatedSettings.isBordered
                    ? "borderBotBlack1"
                    : undefined
                }
              >
                {billColumns.map((column, i) => (
                  <Box
                    key={column}
                    className={classNames(
                      [BillColumns.SNO, BillColumns.QUANTITY, BillColumns.PER_UNIT].includes(
                        column
                      ) && "flex1",

                      [
                        BillColumns.hsCode,
                        BillColumns.DISCOUNT_AMT,
                        BillColumns.VAT_PCT,
                        BillColumns.GROSS_TOTAL
                      ].includes(column) && "flex2",

                      [BillColumns.DESCRIPTION, BillColumns.INFO].includes(column) && "flex3"
                    )}
                    sx={{
                      py: "0.2cm",
                      mx: marginForNameColumn(column),
                      borderLeft: i === 0 && relatedSettings.isBordered ? "1px solid #bbb" : "",
                      borderRight:
                        i === billColumns.length - 1 && relatedSettings.isBordered
                          ? "1px solid #bbb"
                          : "",
                      px: i === 0 || i === billColumns.length - 1 ? "0.2cm" : ""
                    }}
                  >
                    <Typography>
                      <Box sx={{ ...getColumnTextStyles({ index: i, column }) }}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {["perUnit", "grossTotal"].includes(column) && item[column] !== null
                          ? Number(item[column]).toFixed(2)
                          : item[column]
                          ? item[column]
                          : "-"}

                        {showUnitAfterQuantity(column) && item.unit}

                        {column === BillColumns.DESCRIPTION && (item?.teeth?.length ?? 0) > 0 && (
                          <Box component="span" sx={{ ml: "4px", fontSize: "10px" }}>
                            ({`Teeth: ${item?.teeth?.join(", ")}`})
                          </Box>
                        )}

                        {column === BillColumns.DESCRIPTION && (
                          <Box component="span" fontSize={scaleFont("0.23cm")}>
                            {item.serviceCode && ` (${item.serviceCode})`}
                          </Box>
                        )}
                        {column === BillColumns.DESCRIPTION && spField(item)}
                      </Box>

                      {column === "description" && showStockBatchAndExpiryInBillPrint && (
                        <Box
                          sx={{ whiteSpace: "pre-wrap", display: "inline" }}
                          fontSize={scaleFont("0.23cm")}
                        >
                          {(item?.batchId || item.batchInfo?.batchId) &&
                            `Batch ${item?.batchId || item.batchInfo?.batchId}`}

                          {(item?.expiryDate || item.batchInfo?.expiryDate) &&
                            `, Expiring ${moment(
                              item?.expiryDate || item.batchInfo?.expiryDate
                            ).format("YYYY/MM/DD")}`}
                        </Box>
                      )}

                      {column === "description" &&
                        item?.subItems?.map((subitem) => (
                          <Box
                            key={subitem.productId}
                            paddingTop="0.05cm"
                            className="blockContent"
                            fontSize="12px"
                          >
                            {`◦ ${subitem.description}`}
                            {spField(subitem)}
                          </Box>
                        ))}
                      {showSubItemPriceOnPrint &&
                        column === "perUnit" &&
                        item?.subItems?.map((subitem) => (
                          <Typography
                            key={subitem.productId}
                            textAlign="right"
                            paddingTop="0.05cm"
                            className="blockContent"
                            fontSize="12px"
                          >
                            {`${subitem.productData?.grossTotalPrice}`}
                          </Typography>
                        ))}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
        </Box>
      </Box>

      <Box sx={{ display: "flex", pageBreakInside: "avoid" }}>
        <Box width="60%">
          {!hidePaymentMethodOnPrint &&
            (Boolean(billData?.paymentInfo?.paymentMethod) ||
              billData?.paymentInfo?.paymentBreakdown?.length >= 1) && (
              <Box display="flex" alignItems="flex-start" flex={1} gap={1}>
                <Typography fontSize={scaleFont("0.3cm")}>
                  {tl("billing.paymentMethod")}:
                </Typography>
                <Typography fontSize={scaleFont("0.3cm")} flexGrow={1} fontWeight={500}>
                  {startCase(
                    getPaymentMethodLabel({
                      paymentMethod: billData?.paymentInfo?.paymentMethod ?? null,
                      paymentBreakdown: billData.paymentInfo.paymentBreakdown ?? []
                    })
                  )}
                </Typography>
              </Box>
            )}
          <Box display="flex" alignItems="flex-start" flex={1} gap={1}>
            <Typography fontSize={scaleFont("0.3cm")} flexShrink={0}>
              {tl("billing.inWords")}:
            </Typography>
            <Typography fontSize={scaleFont("0.3cm")} flexGrow={1} fontWeight={500}>
              {billData.document.summary.inWords} only
            </Typography>
          </Box>

          <Box mt={1}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Typography fontSize={scaleFont("0.3cm")} flexShrink={0}>
                {tl("billing.printedDateTime")}:
              </Typography>
              <Typography fontSize={scaleFont("0.3cm")} flexGrow={1} fontWeight={500}>
                {moment().format("YYYY/MM/DD")} {moment().format("h:mm a")}
              </Typography>
            </Box>
            {Boolean(billData?.printCount) && user && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography fontSize={scaleFont("0.3cm")} flexShrink={0}>
                  {tl("billing.rePrintUser")}:
                </Typography>
                <Typography fontSize={scaleFont("0.3cm")} flexGrow={1} fontWeight={500}>
                  {capitalizedName(`${user?.firstName} ${user?.lastName}`)}
                </Typography>
              </Box>
            )}
            {billData.document.enteredBy?.name && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography fontSize={scaleFont("0.3cm")} flexShrink={0}>
                  {tl("billing.billedBy")}:
                </Typography>
                <Typography fontSize={scaleFont("0.3cm")} flexGrow={1} fontWeight={500}>
                  {billData.document.enteredBy.name}
                </Typography>
              </Box>
            )}
            <Box mt={0.5} display="flex" alignItems="flex-start">
              {billData.document.enteredBy?.signature ? (
                <img
                  src={billData.document.enteredBy.signature}
                  alt="Service Provider Signature"
                  width="180px"
                  style={{ objectFit: "contain" }}
                />
              ) : (
                <Box
                  sx={{
                    height: 50,
                    width: 150,
                    borderBottom: "1px dashed black"
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Box width="40%">
          <Box paddingBottom="0.2cm" width="100%" textAlign="right">
            <BillPrintSummary
              billData={billData}
              clientBalance={clientBalance}
              totalPerUnitTimesQty={billData.document.summary.totalPerUnitTimesQty}
              discount={billData.document.summary.discount}
              taxAmount={billData.document.summary.taxAmount}
              netTotal={netTotal}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DescribeBillV2;
